/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 3 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Utils
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";

// MUI components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

function KinerjaBidang() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(true);
  const [bidangList, setBidangList] = useState([]);
  const [dinasId, setDinasId] = useState(null);
  const [selectedBulan, setSelectedBulan] = useState(new Date().getMonth() + 1);
  const [selectedTahun, setSelectedTahun] = useState(new Date().getFullYear());

  // Generate tahun list (current year down to 2025)
  const tahunList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2025; i--) {
    tahunList.push(i);
  }

  // List bulan
  const bulanList = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  // Fungsi untuk menghitung tanggal awal dan akhir berdasarkan bulan dan tahun
  const getDateRange = (bulan, tahun) => {
    const startDate = `${tahun}-${String(bulan).padStart(2, "0")}-01`;
    const endDate = new Date(tahun, bulan, 0).toISOString().split("T")[0];
    return { startDate, endDate };
  };

  // Fungsi untuk menghitung hari kerja
  const calculateWorkingDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    let workingDays = 0;
    for (
      let d = new Date(startDate);
      d <= endDate;
      d.setDate(d.getDate() + 1)
    ) {
      const dayOfWeek = d.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDays++;
      }
    }
    return workingDays;
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      fetchBidangList(user.dinas_id, selectedBulan, selectedTahun);
    } else {
      logoutUser(navigate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Fetch ulang data saat bulan atau tahun berubah
  useEffect(() => {
    if (dinasId) {
      fetchBidangList(dinasId, selectedBulan, selectedTahun);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBulan, selectedTahun]);

  const fetchBidangList = async (currentDinasId, bulan, tahun) => {
    try {
      setLoading(true);

      // Hitung tanggal berdasarkan bulan dan tahun yang dipilih
      const { startDate, endDate } = getDateRange(bulan, tahun);
      const hariKerja = calculateWorkingDays(startDate, endDate);

      // Fetch list bidang
      const bidangResponse = await axios.get(
        `${API_ESA_OFFICE}kinerja-organisasi/bidang/${currentDinasId}`
      );
      let bidangList = bidangResponse.data || [];

      // Filter out bidang dengan id terkecil (kepala dinas)
      if (bidangList.length > 0) {
        const minBidangId = Math.min(...bidangList.map((b) => b.bidang_id));
        bidangList = bidangList.filter((b) => b.bidang_id !== minBidangId);
      }

      // Fetch kinerja untuk setiap bidang
      const kinerjaPromises = bidangList.map(async (bidang) => {
        try {
          const kinerjaResponse = await axios.get(
            `${API_ESA_OFFICE}kinerja-organisasi/bidang/${bidang.bidang_id}/kinerja?bulan=${bulan}&tahun=${tahun}`
          );
          const kinerjaData = kinerjaResponse.data;

          // Ambil hari kerja yang benar dari endpoint apel (memperhitungkan hari libur)
          let hariKerjaApel = hariKerja; // Fallback ke hari kerja lokal jika gagal
          if (kinerjaData.pegawai_data?.pegawai?.length > 0) {
            try {
              // Ambil hari kerja dari endpoint apel untuk pegawai pertama (semua pegawai memiliki hari kerja yang sama)
              const firstPegawai = kinerjaData.pegawai_data.pegawai.find(
                (p) => p.uid_RSS
              );
              if (firstPegawai?.uid_RSS) {
                const apelResponse = await axios.get(
                  `${API_ESA_OFFICE}kinerja-organisasi/individu/${firstPegawai.uid_RSS}/apel?bulan=${bulan}&tahun=${tahun}`
                );
                if (apelResponse.data?.hari_kerja) {
                  hariKerjaApel = apelResponse.data.hari_kerja;
                }
              }
            } catch (error) {
              console.error(
                `Error fetching hari kerja from apel endpoint for bidang ${bidang.bidang_id}:`,
                error
              );
              // Gunakan hari kerja lokal sebagai fallback
            }
          }

          // Hitung persentase kehadiran dari endpoint eksternal
          // Menggunakan hari kerja yang sama dengan apel (memperhitungkan hari libur)
          let persentaseKehadiran = 0;
          if (
            kinerjaData.pegawai_data?.pegawai?.length > 0 &&
            kinerjaData.pegawai_data?.nama_dinas
          ) {
            try {
              const kehadiranResponse = await axios.post(
                "https://smartservice.rotendaokab.go.id/api/admin/daftarHadir/instansi/reguler/update",
                {
                  tAwal: startDate,
                  tAkhir: endDate,
                  unitKerja: kinerjaData.pegawai_data.nama_dinas,
                },
                {
                  headers: {
                    "Content-Type": "application/json",
                  },
                  timeout: 30000,
                }
              );

              if (
                kehadiranResponse.data.success === 1 &&
                kehadiranResponse.data.data
              ) {
                const uidRSSList = kinerjaData.pegawai_data.pegawai.map((p) =>
                  String(p.uid_RSS || "")
                );

                // Filter data berdasarkan uid_RSS yang ada di bidang
                const dataBidang = kehadiranResponse.data.data.filter(
                  (item) => {
                    const uid = String(item.uid || item.uid_RSS || "");
                    return uidRSSList.some((rss) => String(rss) === uid);
                  }
                );

                if (dataBidang.length > 0) {
                  // Hitung total hari hadir dari semua pegawai di bidang
                  const totalHariHadir = dataBidang.reduce(
                    (sum, item) => sum + (parseInt(item.hadir) || 0),
                    0
                  );

                  // Hitung persentase kehadiran menggunakan hari kerja dari apel (memperhitungkan hari libur)
                  const totalPegawai = dataBidang.length;
                  if (hariKerjaApel > 0 && totalPegawai > 0) {
                    persentaseKehadiran = parseFloat(
                      (
                        (totalHariHadir / (hariKerjaApel * totalPegawai)) *
                        100
                      ).toFixed(2)
                    );
                  }
                }
              }
            } catch (error) {
              console.error(
                `Error fetching kehadiran data for bidang ${bidang.bidang_id}:`,
                error
              );
              // Jika error, persentase tetap 0
            }
          }

          // Update periode dengan bulan dan tahun yang dipilih
          // Gunakan hari kerja dari apel (memperhitungkan hari libur) untuk konsistensi
          kinerjaData.periode = {
            bulan: bulan,
            tahun: tahun,
            start_date: startDate,
            end_date: endDate,
            hari_kerja: hariKerjaApel, // Gunakan hari kerja yang benar (memperhitungkan hari libur)
          };

          // Hitung ulang persentase apel menggunakan hari kerja yang benar (memperhitungkan hari libur)
          // Konsisten dengan perhitungan individu: persentase = (apel_hadir / (hari_kerja * total_pegawai)) * 100
          if (kinerjaData.statistik && hariKerjaApel > 0) {
            const totalPegawaiApel = kinerjaData.statistik.total_pegawai || 0;
            
            // Hitung ulang persentase apel pagi menggunakan hari kerja yang benar
            if (kinerjaData.statistik.apel_pagi_hadir !== undefined && totalPegawaiApel > 0) {
              const expectedTotalApelPagi = hariKerjaApel * totalPegawaiApel;
              if (expectedTotalApelPagi > 0) {
                kinerjaData.statistik.persentase_apel_pagi = parseFloat(
                  (
                    (kinerjaData.statistik.apel_pagi_hadir / expectedTotalApelPagi) *
                    100
                  ).toFixed(2)
                );
              } else {
                kinerjaData.statistik.persentase_apel_pagi = 0;
              }
            }
            
            // Hitung ulang persentase apel siang menggunakan hari kerja yang benar
            if (kinerjaData.statistik.apel_siang_hadir !== undefined && totalPegawaiApel > 0) {
              const expectedTotalApelSiang = hariKerjaApel * totalPegawaiApel;
              if (expectedTotalApelSiang > 0) {
                kinerjaData.statistik.persentase_apel_siang = parseFloat(
                  (
                    (kinerjaData.statistik.apel_siang_hadir / expectedTotalApelSiang) *
                    100
                  ).toFixed(2)
                );
              } else {
                kinerjaData.statistik.persentase_apel_siang = 0;
              }
            }
          }

          // Tambahkan persentase kehadiran ke statistik
          if (kinerjaData.statistik) {
            kinerjaData.statistik.persentase_kehadiran = persentaseKehadiran;
          }

          return {
            ...bidang,
            kinerja: kinerjaData,
          };
        } catch (error) {
          console.error(
            `Error fetching kinerja for bidang ${bidang.bidang_id}:`,
            error
          );
          return {
            ...bidang,
            kinerja: null,
          };
        }
      });

      const bidangWithKinerja = await Promise.all(kinerjaPromises);
      setBidangList(bidangWithKinerja);
    } catch (error) {
      console.error("Error fetching bidang list:", error);
      swal({
        title: "Error",
        text: "Gagal memuat data bidang",
        icon: "error",
        button: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDetailClick = (bidangId) => {
    navigate(`/kinerja-organisasi/bidang/${bidangId}/detail`);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <MDTypography variant="h4" fontWeight="bold">
              Kinerja Bidang
            </MDTypography>
            <MDBox display="flex" gap={2} alignItems="center">
              <Autocomplete
                options={bulanList}
                value={bulanList.find((b) => b.value === selectedBulan) || null}
                onChange={(event, newValue) => {
                  if (newValue) setSelectedBulan(newValue.value);
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Bulan"
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      minWidth: 150,
                    }}
                  />
                )}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
              />
              <Autocomplete
                options={tahunList}
                value={selectedTahun}
                onChange={(event, newValue) => {
                  if (newValue) setSelectedTahun(newValue);
                }}
                renderInput={(params) => (
                  <MDInput
                    {...params}
                    label="Tahun"
                    variant="outlined"
                    sx={{
                      backgroundColor: "white",
                      minWidth: 120,
                    }}
                  />
                )}
                getOptionLabel={(option) => option.toString()}
              />
            </MDBox>
          </MDBox>
          <MDBox mb={2}>
            <MDTypography variant="body2" color="text">
              Periode: {bulanList.find((b) => b.value === selectedBulan)?.label}{" "}
              {selectedTahun}
            </MDTypography>
          </MDBox>
        </MDBox>

        <Grid container spacing={3}>
          {bidangList.map((bidang, idx) => {
            const kinerja = bidang.kinerja?.statistik || {};
            const bidangInfo = bidang.kinerja?.bidang || {};
            const profilKepala = bidangInfo.uid_RSS
              ? `https://smartdh.rotendaokab.go.id/uploads/${bidangInfo.uid_RSS}.jpg`
              : null;

            return (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: 3,
                    "&:hover": {
                      boxShadow: 6,
                      transform: "translateY(-4px)",
                      transition: "all 0.3s ease",
                    },
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Header dengan foto kepala bidang */}
                    <MDBox
                      display="flex"
                      alignItems="center"
                      mb={2}
                      pb={2}
                      sx={{ borderBottom: "1px solid rgba(0,0,0,0.1)" }}
                    >
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ mr: 2 }}
                      >
                        {profilKepala ? (
                          <>
                            <img
                              src={profilKepala}
                              alt={bidangInfo.nama_kepala || "Kepala Bidang"}
                              style={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "2px solid #e0e0e0",
                              }}
                              onError={(e) => {
                                // Jika gambar tidak ditemukan, tampilkan icon default
                                e.target.style.display = "none";
                                if (e.target.nextSibling) {
                                  e.target.nextSibling.style.display = "flex";
                                }
                              }}
                            />
                            <MDBox
                              display="none"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                backgroundColor: "grey.200",
                                color: "grey.500",
                              }}
                            >
                              <Icon sx={{ fontSize: 32 }}>person</Icon>
                            </MDBox>
                          </>
                        ) : (
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                              width: 64,
                              height: 64,
                              borderRadius: "50%",
                              backgroundColor: "grey.200",
                              color: "grey.500",
                            }}
                          >
                            <Icon sx={{ fontSize: 32 }}>person</Icon>
                          </MDBox>
                        )}
                      </MDBox>
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="bold">
                          {bidang.nama_bidang}
                        </MDTypography>
                        {bidangInfo.nama_kepala && (
                          <MDTypography variant="body2" color="text">
                            {bidangInfo.nama_kepala}
                          </MDTypography>
                        )}
                      </MDBox>
                    </MDBox>

                    {/* Statistik Agregat */}
                    <Grid container spacing={2} mb={2}>
                      <Grid item xs={6}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(0, 123, 255, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            color="info"
                          >
                            {kinerja.total_pegawai || 0}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Pegawai
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(40, 167, 69, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            color="success"
                          >
                            {kinerja.total_layanan || 0}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Layanan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(255, 193, 7, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            color="warning"
                          >
                            {kinerja.total_tamu || 0}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Tamu
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(220, 53, 69, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            color={
                              (kinerja.persentase_disposisi || 0) >= 80
                                ? "success"
                                : (kinerja.persentase_disposisi || 0) >= 60
                                ? "warning"
                                : "error"
                            }
                          >
                            {kinerja.persentase_disposisi !== undefined
                              ? `${kinerja.persentase_disposisi}%`
                              : "0%"}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mb={0.5}
                          >
                            Persentase Disposisi
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            ({kinerja.total_disposisi_selesai || 0}/
                            {kinerja.total_disposisi || 0})
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(108, 117, 125, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            color={
                              (kinerja.persentase_instruksi || 0) >= 80
                                ? "success"
                                : (kinerja.persentase_instruksi || 0) >= 60
                                ? "warning"
                                : "error"
                            }
                          >
                            {kinerja.persentase_instruksi !== undefined
                              ? `${kinerja.persentase_instruksi}%`
                              : "0%"}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mb={0.5}
                          >
                            Persentase Instruksi/Tugas
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            ({kinerja.total_instruksi_selesai || 0}/
                            {kinerja.total_instruksi || 0})
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={6}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(138, 43, 226, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            color="info"
                          >
                            {kinerja.total_event || 0}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text"
                            display="block"
                            mb={0.5}
                          >
                            Event
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            {kinerja.total_peserta_event || 0} peserta
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(23, 162, 184, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            color="info"
                          >
                            {kinerja.persentase_kehadiran
                              ? `${kinerja.persentase_kehadiran.toFixed(2)}%`
                              : "0.00%"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Persentase Kehadiran
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(102, 187, 106, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDBox mb={0.5}>
                            <MDTypography
                              variant="body1"
                              fontWeight="bold"
                              color="success"
                            >
                              Pagi:{" "}
                              {kinerja.persentase_apel_pagi
                                ? `${kinerja.persentase_apel_pagi.toFixed(2)}%`
                                : "0.00%"}
                            </MDTypography>
                            <MDTypography
                              variant="body1"
                              fontWeight="bold"
                              color="info"
                            >
                              Siang:{" "}
                              {kinerja.persentase_apel_siang
                                ? `${kinerja.persentase_apel_siang.toFixed(2)}%`
                                : "0.00%"}
                            </MDTypography>
                          </MDBox>
                          <MDTypography variant="caption" color="text">
                            Persentase Apel
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12}>
                        <MDBox
                          p={1.5}
                          borderRadius="lg"
                          sx={{
                            backgroundColor: "rgba(156, 39, 176, 0.1)",
                            textAlign: "center",
                          }}
                        >
                          <MDTypography
                            variant="h5"
                            fontWeight="bold"
                            sx={{ color: "#9c27b0" }}
                          >
                            {kinerja.persentase_laporan
                              ? `${kinerja.persentase_laporan.toFixed(2)}%`
                              : "0.00%"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text">
                            Persentase Laporan Kinerja
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>

                    {/* Tombol Detail */}
                    <MDButton
                      variant="gradient"
                      color="info"
                      size="medium"
                      fullWidth
                      onClick={() => handleDetailClick(bidang.bidang_id)}
                      sx={{ mt: 2 }}
                    >
                      <Icon fontSize="small">visibility</Icon>
                      &nbsp;Lihat Detail
                    </MDButton>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default KinerjaBidang;
